import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import TileWMS from "ol/source/TileWMS";
import OSM from "ol/source/OSM";
import { fromLonLat } from "ol/proj";
import "ol/ol.css";
import { API_BASE, GS_WMS } from "./config/api";
import AppHeader from "./components/TopBar";
import { APP_COLORS as C } from "./theme/colors";

// ── All WMS layers (same as App.jsx layerGroups) ──────────
const WMS_LAYERS = [
  { key: "ga_boundary", wms: "haryanagas:ga_boundary" },
  { key: "ca_boundary", wms: "haryanagas:ca_boundary" },
  { key: "cng_boundary", wms: "haryanagas:cng_boundary" },
  { key: "cng_station", wms: "haryanagas:cng_station" },
  { key: "compressor", wms: "haryanagas:compressor" },
  // { key: "cgs", wms: "haryanagas:cgs" },
  { key: "dispenser", wms: "haryanagas:dispenser" },
  { key: "odorizer", wms: "haryanagas:odorizer" },
  { key: "cascade", wms: "haryanagas:cascade" },
  { key: "ci", wms: "haryanagas:ci" },
  { key: "dpng_survey", wms: "haryanagas:dpng_survey" },
  { key: "pole_marker", wms: "haryanagas:pole_marker" },
  { key: "rcc_marker", wms: "haryanagas:rcc_marker" },
  { key: "road", wms: "haryanagas:road" },
  { key: "house", wms: "haryanagas:house" },
  { key: "connection_pit", wms: "haryanagas:connection_pit" },
  { key: "electric_pole", wms: "haryanagas:electric_pole" },
  { key: "steel_pipelines", wms: "haryanagas:steel_pipelines" },
  { key: "tlp", wms: "haryanagas:tlp" },
  { key: "mdpe_pipelines", wms: "haryanagas:mdpe_pipelines" },
  { key: "valve", wms: "haryanagas:valve" },
];

// ── Stat layer definitions ────────────────────────────────
const LAYERS = {
  steel_pipelines: {
    label: "Steel Pipeline",
    wms: "haryanagas:steel_pipelines",
    type: "length",
    unit: "km",
    icon: "🔩",
    color: "#dc2626",
    bg: "#fef2f2",
    border: "#fecaca",
  },
  dpng_survey: {
    label: "Domestic Customer",
    wms: "haryanagas:dpng_survey",
    type: "count",
    unit: "",
    icon: "📋",
    color: "#2563eb",
    bg: "#eff6ff",
    border: "#bfdbfe",
  },
  cng_station: {
    label: "CNG Station",
    wms: "haryanagas:house",
    type: "count",
    unit: "",
    icon: "🏡",
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#bbf7d0",
  },
  ci: {
    label: "Industrial & Commercial Customer",
    wms: "haryanagas:ci",
    type: "count",
    unit: "",
    icon: "🏭",
    color: "#9333ea",
    bg: "#faf5ff",
    border: "#e9d5ff",
  },
  mdpe_valve: {
    label: "MDPE Valve ",
    wms: "haryanagas:valve",
    type: "count",
    unit: "",
    icon: "⛽",
    color: "#ea580c",
    bg: "#fff7ed",
    border: "#fed7aa",
  },
  mdpe_pipelines: {
    label: "MDPE Pipeline",
    wms: "haryanagas:mdpe_pipelines",
    type: "length",
    unit: "km",
    icon: "🔵",
    color: "#0891b2",
    bg: "#ecfeff",
    border: "#a5f3fc",
  },
  tlp: {
    label: "TLP",
    wms: "haryanagas:tlp",
    type: "count",
    unit: "",
    icon: "⚡",
    color: "#ca8a04",
    bg: "#fefce8",
    border: "#fef08a",
  },
  cascade: {
    label: "Cascade",
    wms: "haryanagas:cascade",
    type: "count",
    unit: "",
    icon: "🔗",
    color: "#db2777",
    bg: "#fdf2f8",
    border: "#fbcfe8",
  },
  valve: {
    label: "Valve",
    wms: "haryanagas:valve",
    type: "count",
    unit: "",
    icon: "🔧",
    color: "#0891b2",
    bg: "#ecfeff",
    border: "#a5f3fc",
  },
  compressor: {
    label: "Compressor",
    wms: "haryanagas:compressor",
    type: "count",
    unit: "",
    icon: "⚙️",
    color: "#7c3aed",
    bg: "#f5f3ff",
    border: "#ddd6fe",
  },
  connection_pit: {
    label: "Connection Pit",
    wms: "haryanagas:connection_pit",
    type: "count",
    unit: "",
    icon: "🕳️",
    color: "#0369a1",
    bg: "#f0f9ff",
    border: "#bae6fd",
  },
  dispenser: {
    label: "dispenser",
    wms: "haryanagas:dispenser",
    type: "count",
    unit: "",
    icon: "🚿",
    color: "#059669",
    bg: "#ecfdf5",
    border: "#a7f3d0",
  },
  cgs: {
    label: "CGS",
    wms: "haryanagas:cgs",
    type: "count",
    unit: "",
    icon: "🏗️",
    color: "#b91c1c",
    bg: "#fef2f2",
    border: "#fecaca",
  },
  electric_pole: {
    label: "Electric Pole",
    wms: "haryanagas:electric_pole",
    type: "count",
    unit: "",
    icon: "🔌",
    color: "#d97706",
    bg: "#fffbeb",
    border: "#fde68a",
  },
  pole_marker: {
    label: "Pole Marker",
    wms: "haryanagas:rcc_marker",
    type: "count",
    unit: "",
    icon: "📌",
    color: "#7c3aed",
    bg: "#f5f3ff",
    border: "#ddd6fe",
  },
  odorizer: {
    label: "DRS",
    wms: "haryanagas:odorizer",
    type: "count",
    unit: "",
    icon: "💨",
    color: "#0f766e",
    bg: "#f0fdfa",
    border: "#99f6e4",
  },
};

// ============================================================
// MINI OPENLAYERS MAP — same layers as App.jsx
// ============================================================
function MiniOLMap() {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    // Base layer
    const baseLayer = new TileLayer({
      source: new OSM({ crossOrigin: "anonymous" }),
    });

    // All WMS layers (same as App.jsx)
    const wmsLayers = WMS_LAYERS.map(
      ({ wms }) =>
        new TileLayer({
          source: new TileWMS({
            url: GS_WMS,
            params: {
              LAYERS: wms,
              FORMAT: "image/png8",
              TRANSPARENT: true,
              TILED: true,
            },
            serverType: "geoserver",
            crossOrigin: "anonymous",
            transition: 0,
          }),
          preload: 2,
        }),
    );

    const map = new Map({
      target: mapRef.current,
      layers: [baseLayer, ...wmsLayers],
      controls: [], // no default controls — clean look
      view: new View({
        center: fromLonLat([76.993869, 28.448841]),
        zoom: 10,
        maxZoom: 20,
        minZoom: 2,
        constrainResolution: true,
      }),
    });

    mapInstance.current = map;

    // resize when parent changes
    const ro = new ResizeObserver(() => map.updateSize());
    ro.observe(mapRef.current);

    return () => {
      ro.disconnect();
      map.setTarget(null);
      map.dispose();
      mapInstance.current = null;
    };
  }, []);

  // zoom helpers
  const zoomIn = () => {
    const v = mapInstance.current?.getView();
    v && v.animate({ zoom: v.getZoom() + 1, duration: 250 });
  };
  const zoomOut = () => {
    const v = mapInstance.current?.getView();
    v && v.animate({ zoom: v.getZoom() - 1, duration: 250 });
  };
  const home = () => {
    const v = mapInstance.current?.getView();
    v &&
      v.animate({
        center: fromLonLat([76.993869, 28.448841]),
        zoom: 10,
        duration: 500,
      });
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: C.background,
        borderRadius: "0 0 12px 12px",
        overflow: "hidden",
      }}
    >
      {/* OL map canvas */}
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />

      {/* Map controls overlay */}
      <div
        style={{
          position: "absolute",
          top: 8,
          left: 8,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          zIndex: 10,
        }}
      >
        {[
          { label: "⌂", title: "Home", onClick: home },
          { label: "+", title: "Zoom In", onClick: zoomIn },
          { label: "−", title: "Zoom Out", onClick: zoomOut },
        ].map((btn) => (
          <button
            key={btn.label}
            title={btn.title}
            onClick={btn.onClick}
            style={{
              width: 28,
              height: 28,
              background: "#fff",
              border: "1px solid #cbd5e1",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
              color: "#374151",
              lineHeight: 1,
            }}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Live badge */}
      <div
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "rgba(15,23,42,0.85)",
          border: "1px solid #334155",
          borderRadius: 6,
          padding: "3px 10px",
          fontSize: 10,
          fontWeight: 600,
          color: "#4ade80",
          display: "flex",
          alignItems: "center",
          gap: 5,
          zIndex: 10,
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: C.accent,
            animation: "livepulse 2s infinite",
          }}
        />
        Live GIS
      </div>
    </div>
  );
}

// ============================================================
// ANIMATED COUNT-UP
// ============================================================
function CountUp({ target, isFloat }) {
  if (target == null) return <span style={{ color: "#9ca3af" }}>—</span>;
  return (
    <span>
      {isFloat
        ? parseFloat(target).toFixed(2)
        : Math.round(target).toLocaleString("en-IN")}
    </span>
  );
}

// ============================================================
// MAIN DASHBOARD COMPONENT
// ============================================================
export default function Dashboard({ onNavigateToMap, onBackToHome }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [steelChart, setSteelChart] = useState([]);
  const [mdpeChart, setMdpeChart] = useState([]);
  const [chartLoad, setChartLoad] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [gaList, setGaList] = useState([]);
  const [caList, setCaList] = useState([]);
  const [selectedGA, setSelectedGA] = useState("1");
  const [selectedCA, setSelectedCA] = useState("ALL");

  // GA list fetch
  useEffect(() => {
    (async () => {
      try {
        const data = await (await fetch(`${API_BASE}/ga_boundary`)).json();
        const list = Object.entries(data)
          .map(([id, name]) => ({ id, name }))
          .filter((ga) => ga.id === "1"); // ← sirf ID=1 rakho
        setGaList(list);
      } catch {
        setGaList([]);
      }
    })();
  }, []);

  // CA list fetch jab GA change ho
  useEffect(() => {
    if (!selectedGA || selectedGA === "ALL") {
      setCaList([]);
      setSelectedCA("ALL");
      return;
    }
    (async () => {
      try {
        const data = await (
          await fetch(`${API_BASE}/ca_boundary?ga_boundary_id=${selectedGA}`)
        ).json();
        const list = data
          ? Object.entries(data).map(([id, name]) => ({ id, name }))
          : [];
        setCaList(list);
        setSelectedCA("ALL");
      } catch {
        setCaList([]);
      }
    })();
  }, [selectedGA]);

  // ── fetch main stats ──────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setLoading(true);
    setChartLoad(true);
    const params = new URLSearchParams();
    if (selectedGA !== "ALL") params.set("ga_boundary_id", selectedGA);
    if (selectedCA !== "ALL") params.set("ca_boundary_id", selectedCA);

    let data;
    try {
      data = await (
        await fetch(`${API_BASE}/dashboard_summary?${params}`)
      ).json();
    } catch (err) {
      console.error("API fetch failed:", err);
      setLoading(false);
      setChartLoad(false);
      return;
    }

    setStats({
      steel_pipelines: parseFloat(data.steel_pipeline_km),
      dpng_survey: parseInt(data.dpng_count), // dpng_count API field
      cng_station: parseInt(data.cng_station_count), // cng_station_count API field
      ci: parseInt(data.industrial_customer_count),
      mdpe_valve: parseInt(data.mdpe_valve_count), // mdpe_valve_count API field
      mdpe_pipelines: parseFloat(data.mdpe_pipeline_km),
      tlp: parseInt(data.tlp_count),
      compressor: parseInt(data.compressor_count),
      dispenser: parseInt(data.dispensor_count),
      connection_pit: parseInt(data.connection_pit_count ?? 0),
      odorizer: parseInt(data.drs_count ?? 0),
    });
    // Bargraph data bhi API se parse karo
    const steelRaw = data.steel_pipeline_bargraph
      ? JSON.parse(data.steel_pipeline_bargraph)
      : [];
    const mdpeRaw = data.mdpe_pipeline_bargraph
      ? JSON.parse(data.mdpe_pipeline_bargraph)
      : [];

    const steelColors = ["#fca5a5", "#f87171", "#ef4444", "#e11d48", "#b91c1c"];
    const mdpeColors = ["#bfdbfe", "#60a5fa", "#3b82f6", "#2563eb", "#1d4ed8"];

    setSteelChart(
      (steelRaw ?? []).map((d, i) => ({
        label: d.key,
        value: d.value,
        color: steelColors[i] ?? "#dc2626",
      })),
    );
    setMdpeChart(
      (mdpeRaw ?? []).map((d, i) => ({
        label: d.key,
        value: d.value,
        color: mdpeColors[i] ?? "#0891b2",
      })),
    );
    setLoading(false);
    setChartLoad(false);
    setLastUpdated(new Date().toLocaleTimeString("en-IN"));
  }, [selectedGA, selectedCA]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const doRefresh = () => {
    fetchStats();
  };

  // ── shared tooltip style ──────────────────────────────────
  const tooltipStyle = {
    contentStyle: {
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 8,
      fontSize: 12,
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
    formatter: (v) => [v.toLocaleString("en-IN"), "Length(km)"],
    cursor: { fill: "rgba(0,0,0,0.04)" },
  };

  // ── RENDER ────────────────────────────────────────────────
  return (
    <div style={ROOT}>
      <style>{`
        @keyframes livepulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
      `}</style>

      <AppHeader />

      {/* ── TOP BAR ── */}
      {/* <div style={TOPBAR}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src={`${process.env.PUBLIC_URL}/assets/HCG-logo-1.png`}
            alt="HCG"
            style={{
              height: 20,
              width: "auto",
              objectFit: "contain",
              padding: "2px",
            }}
          />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#e2e8f0" }}>
              BlueView | Haryana City Gas Distribution
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {lastUpdated && (
            <span style={{ fontSize: 11, color: "#e2e5eb" }}>
              🕐 {lastUpdated}
            </span>
          )}
          {onNavigateToMap && (
            <button onClick={onNavigateToMap} style={BTN_BLUE}>
              🗺 Open Full Map
            </button>
          )}
          <button
            onClick={doRefresh}
            disabled={loading}
            style={{ ...BTN_ORANGE, opacity: loading ? 0.7 : 1 }}
          >
            {loading ? "⏳ Loading…" : "Refresh"}
          </button>
          {onBackToHome && (
            <button onClick={onBackToHome} style={BTN_GRAY}>
              Home
            </button>
          )}
        </div>
      </div> */}

      {/* ── BODY ── */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* MAIN CONTENT */}
        <div style={CONTENT}>
          {/* ROW 1 — 6 top stat cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6,1fr)",
              gap: 8,
              flexShrink: 0,
            }}
          >
            {[
              "steel_pipelines",
              "dpng_survey",
              "cng_station",
              "ci",
              "mdpe_valve",
              "mdpe_pipelines",
            ].map((k) => {
              const cfg = LAYERS[k];
              const val = stats[k] ?? null;
              return (
                <div
                  key={k}
                  style={{
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderTop: `2px solid ${cfg.color}`,
                    borderRadius: 8,
                    padding: "10px 14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 13,
                          color: "#0e0f0f",
                          fontWeight: 900,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: 6,
                        }}
                      >
                        {cfg.label}
                      </div>
                      <div
                        style={{
                          fontSize: 24,
                          fontWeight: 800,
                          color: cfg.color,
                          lineHeight: 1,
                        }}
                      >
                        {loading ? (
                          <span style={{ fontSize: 14, color: "#9ca3af" }}>
                            Loading…
                          </span>
                        ) : (
                          <>
                            <CountUp
                              target={val}
                              isFloat={cfg.type === "length"}
                            />
                            {cfg.unit && (
                              <span
                                style={{
                                  fontSize: 12,
                                  color: "#9ca3af",
                                  marginLeft: 4,
                                  fontWeight: 500,
                                }}
                              >
                                {cfg.unit}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ROW 2 — Left panel + Map + Right panel */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "320px minmax(0,1fr) 320px",
              gap: 8,
              flex: 1,
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {/* LEFT PANEL */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                overflow: "hidden",
              }}
            >
              {/* Steel bar chart */}
              <div style={CHART_CARD}>
                <div style={CHART_TITLE}>
                  Steel Pipeline
                  <br />
                  <span
                    style={{
                      color: "#818386",
                      textTransform: "none",
                      letterSpacing: 0,
                      fontWeight: 700,
                    }}
                  >
                    Dia Wise
                  </span>
                </div>
                {chartLoad ? (
                  <div style={LOADING_TEXT}>Fetching…</div>
                ) : (
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={steelChart}
                        margin={{ top: 4, right: 4, left: -28, bottom: 0 }}
                      >
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11, fill: "#000000" }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: "#000000" }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip {...tooltipStyle} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {steelChart.map((d, i) => (
                            <Cell key={i} fill={d.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            {/* CENTER — Real OL Map + WMS Legend */}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                overflow: "hidden",
                minHeight: 0,
              }}
            >
              {/* Filter bar — GREEN BOX — map ke upar alag */}
              <div
                style={{
                  background: C.primary,
                  border: `1px solid ${C.primaryDark}`,
                  borderRadius: 8,
                  padding: "8px 12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{ fontWeight: 700, fontSize: 15, color: C.textOnPrimary }}
                >
                  Haryana Gas Network
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {/* {gaList.length > 1 && ( */}
                  <select
                    value={selectedGA}
                    onChange={(e) => {
                      setSelectedGA(e.target.value);
                      setSelectedCA("ALL");
                    }}
                    style={{
                      fontSize: 15,
                      padding: "3px 6px",
                      borderRadius: 6,
                      border: `1px solid ${C.border}`,
                      color: C.primary,
                      background: C.surface,
                      cursor: "pointer",
                      minWidth: 120,
                    }}
                  >
                    <option value="ALL">All GA</option>
                    {gaList.map((ga) => (
                      <option key={ga.id} value={ga.id}>
                        Gurgaon
                      </option>
                    ))}
                  </select>

                  <select
                    value={selectedCA}
                    onChange={(e) => setSelectedCA(e.target.value)}
                    style={{
                      fontSize: 15,
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: `1px solid ${C.border}`,
                      color: C.primary,
                      background: C.surface,
                      cursor: "pointer",
                      minWidth: 120,
                    }}
                  >
                    <option value="ALL">All CA</option>
                    {(caList ?? []).map((ca) => (
                      <option key={ca.id} value={ca.id}>
                        {ca.name}
                      </option>
                    ))}
                  </select>

                  {(selectedGA !== "ALL" || selectedCA !== "ALL") && (
                    <button
                      onClick={() => {
                        setSelectedGA("1");
                        setSelectedCA("ALL");
                      }}
                      style={{
                        fontSize: 10,
                        padding: "4px 8px",
                        borderRadius: 6,
                        border: "1px solid #fca5a5",
                        background: "#fef2f2",
                        color: "#dc2626",
                        cursor: "pointer",
                      }}
                    >
                      ✕ Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Map — RED BOX — full remaining height */}
              <div
                style={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderTop: `2px solid ${C.primary}`,
                  borderRadius: 8,
                  overflow: "hidden",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
                  <MiniOLMap />
                </div>
              </div>
            </div>

            {/* RIGHT PANEL */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                overflow: "hidden",
              }}
            >
              {/* MDPE bar chart */}
              <div style={CHART_CARD}>
                <div style={CHART_TITLE}>
                  MDPE Pipeline
                  <br />
                  <span
                    style={{
                      color: "#818386",
                      textTransform: "none",
                      letterSpacing: 0,
                      fontWeight: 700,
                    }}
                  >
                    Dia Wise
                  </span>
                </div>
                {chartLoad ? (
                  <div style={LOADING_TEXT}>Fetching…</div>
                ) : (
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={mdpeChart}
                        margin={{ top: 4, right: 4, left: -28, bottom: 0 }}
                      >
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11, fill: "#09090a" }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: "#09090a" }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip {...tooltipStyle} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {mdpeChart.map((d, i) => (
                            <Cell key={i} fill={d.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ROW 3 — Bottom 6 cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5,1fr)",
              gap: 8,
              flexShrink: 0,
            }}
          >
            {[
              "tlp",
              "connection_pit",
              "compressor",
              "dispenser",
              "odorizer",
            ].map((k) => {
              const cfg = LAYERS[k];
              return (
                <div
                  key={k}
                  style={{
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderBottom: `2px solid ${cfg.color}`,
                    borderRadius: 8,
                    padding: "10px 14px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      color: "#0b0b0c",
                      fontWeight: 900,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: 4,
                    }}
                  >
                    {cfg.label}
                  </div>
                  <div
                    style={{ fontSize: 22, fontWeight: 800, color: cfg.color }}
                  >
                    {loading ? (
                      "…"
                    ) : (
                      <CountUp target={stats[k] ?? null} isFloat={false} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SHARED STYLE TOKENS
// ============================================================
const ROOT = {
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  width: "100vw",
  background: C.background,
  fontFamily: "'Inter',system-ui,sans-serif",
  overflow: "hidden",
};
const TOPBAR = {
  background: C.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 20px",
  flexShrink: 0,
  borderBottom: `2px solid ${C.primaryDark}`,
};
const CONTENT = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  padding: 8,
  gap: 8,
};
const CHART_CARD = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderTop: `2px solid ${C.primary}`,
  borderRadius: 8,
  padding: "12px",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minHeight: 0,
};
const CHART_TITLE = {
  fontSize: 15,
  fontWeight: 900,
  color: C.primary,
  marginBottom: 8,
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  flexShrink: 0,
};
const LOADING_TEXT = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: C.textMuted,
  fontSize: 12,
};
const BTN_BLUE = {
  background: C.primary,
  color: C.textOnPrimary,
  border: "none",
  borderRadius: 6,
  padding: "6px 14px",
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
};
const BTN_ORANGE = {
  background: C.accent,
  color: C.textOnPrimary,
  border: "none",
  borderRadius: 6,
  padding: "6px 14px",
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
};
const BTN_GRAY = {
  background: C.primaryMid,
  color: C.textOnPrimary,
  border: "none",
  borderRadius: 6,
  padding: "6px 14px",
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
};
